import classNames from "classnames";
import React from "react";

import { SpaceCategoryBeds, SpaceCategoryGenderRestriction } from "gql/types";
import { useTranslation } from "lib/i18n/client";

import { SpaceCategoryFeaturesSpaceCategoryFragment } from "./SpaceCategoryFeatures.generated";
import styles from "./SpaceCategoryFeatures.module.css";

const SpaceCategoryFeatures: React.FC<SpaceCategoryFeatureProps> = ({
  assetType,
  features: {
    capacity,
    size,
    bedrooms,
    bathrooms,
    terrace,
    balcony,
    entireSpace,
    genderRestriction,
    beds,
    entireSpaceCapacity,
  },
  className,
}) => {
  const { t } = useTranslation("spaceCategory");
  assetType = assetType.toLowerCase();
  const showGenderRestriction =
    genderRestriction &&
    assetType === "hostel" &&
    (!entireSpace ||
      (entireSpace &&
        genderRestriction !== SpaceCategoryGenderRestriction.Unspecified));

  return (
    <ul className={classNames(styles.spaceCategoryFeatures, className)}>
      {["apartment", "aparthotel"].includes(assetType) && (
        <li>
          <IconBedroom />
          {bedrooms > 0
            ? `${t("features.rooms", {
                count: bedrooms,
              })}`
            : t("features.studio")}
        </li>
      )}
      {["hostel"].includes(assetType) && (
        <li>
          <IconBedroom />
          {entireSpace
            ? `${t("features.private_space")}`
            : t("features.individualBed", { capacity: entireSpaceCapacity })}
        </li>
      )}
      {showGenderRestriction && (
        <li>
          <IconGenderRestriction />
          {t(`features.gender_restriction.${genderRestriction.toLowerCase()}`)}
        </li>
      )}
      {(assetType !== "hostel" || entireSpace) && (
        <li>
          <IconPeople />
          {t("features.people", { count: capacity })}
        </li>
      )}
      {["apartment", "aparthotel"].includes(assetType) && bathrooms > 0 && (
        <li>
          <IconBathroom />
          {t("features.bathrooms", { count: bathrooms })}
        </li>
      )}
      {assetType === "hostel" && (
        <li>
          <IconBathroom />
          {bathrooms > 0
            ? t("features.bathroomPrivate")
            : t("features.bathroomShared")}
        </li>
      )}
      {size > 0 && (
        <li>
          <IconSize />
          {t("features.size", { count: size })}
        </li>
      )}
      {terrace && (
        <li>
          <IconTerrace />
          {t("features.terrace")}
        </li>
      )}
      {balcony && (
        <li>
          <IconBalcony />
          {t("features.balcony")}
        </li>
      )}
      <li>
        <IconBed />
        {getAvailableBedsMessage(beds)}
      </li>
    </ul>
  );
};

const getAvailableBedsMessage = (beds: SpaceCategoryBeds) => {
  const { t } = useTranslation("spaceCategory");
  const availableBeds = [];

  if (beds.double > 0) {
    availableBeds.push(
      t("features.beds.doubleBed", {
        count: beds.double,
      }),
    );
  }
  if (beds.single > 0) {
    availableBeds.push(
      t("features.beds.singleBed", {
        count: beds.single,
      }),
    );
  }
  if (beds.twin > 0) {
    availableBeds.push(
      t("features.beds.twinBed", {
        count: beds.twin,
      }),
    );
  }
  if (beds.twinOrDouble > 0) {
    availableBeds.push(
      t("features.beds.twinOrDoubleBed", {
        count: beds.twinOrDouble,
        twins: beds.twinOrDouble * 2,
      }),
    );
  }
  if (beds.bunkBed > 0) {
    availableBeds.push(
      t("features.beds.bunkBed", {
        count: beds.bunkBed,
      }),
    );
  }
  if (beds.foldingDoubleBed > 0) {
    availableBeds.push(
      t("features.beds.foldingDoubleBed", {
        count: beds.foldingDoubleBed,
      }),
    );
  }
  if (beds.trundleDoubleBed > 0) {
    availableBeds.push(
      t("features.beds.trundleDoubleBed", {
        count: beds.trundleDoubleBed,
      }),
    );
  }
  if (beds.sofaBed > 0) {
    availableBeds.push(
      t("features.beds.sofaBed", {
        count: beds.sofaBed,
      }),
    );
  }
  return availableBeds
    .join(", ")
    .replace(/,([^,]*)$/, ` ${t("features.beds.and")}$1`);
};

const IconBedroom = () => (
  <svg viewBox="0 0 32 32">
    <path d="M4 15V4" />
    <path d="M28 15V4" />
    <path d="M4 7H28" />
    <path d="M16 15V7" />
    <path d="M31 15H1V23H31V15Z" />
    <path d="M1 23V28" />
    <path d="M31 23V28" />
  </svg>
);

type SpaceCategoryFeatureProps = {
  assetType: string;
  features: SpaceCategoryFeaturesSpaceCategoryFragment;
  className?: string;
};

const IconBathroom = () => (
  <svg viewBox="0 0 32 32">
    <path d="M9 28v3M23 28v3M31 19H1v2c0 4 3 7 7 7h16c4 0 7-3 7-7v-2h0ZM23 6c0-2 2-4 4-4s4 2 4 4v13" />
    <path d="M19 10c0-2 2-4 4-4s4 2 4 4h-8ZM19 14v1M23 14v1M27 14v1" />
  </svg>
);

const IconBed = () => (
  <svg viewBox="0 0 32 32">
    <path d="M1 11H7C8.7 11 10 12.3 10 14V17" />
    <path d="M1 4V28" />
    <path d="M31 10V28" />
    <path d="M1 22H31" />
    <path d="M1 17H31" />
  </svg>
);

const IconPeople = () => (
  <svg viewBox="0 0 32 32">
    <path d="M12 15a5 5 0 1 0 0-11 5 5 0 0 0 0 11ZM4 28v-3a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v3M21 4a5 5 0 0 1 0 11M28 28v-3a5 5 0 0 0-4-5" />
  </svg>
);

const IconTerrace = () => (
  <svg viewBox="0 0 32 32">
    <g>
      <path d="M16 31.3075V22.9857V9.15472M13 31.3075H19M15.1227 1.00194C23.2303 0.886198 28.3611 5.98689 29.2222 6.87231L27.7593 8.76351H23.853L22.3877 6.93018M15.1227 1.00194C7.73727 1.11768 3.0162 5.47879 2 6.4649L3.75463 8.76351H7.55555L9.11458 6.97532M15.1227 1.00194C14.3542 1.43828 12.274 2.93945 11.2474 4.02047C10.4503 4.86038 9.64331 5.94518 9.11458 6.97532M9.11458 6.97532L10.8507 8.76351H20.8657L22.3877 6.93018M22.3877 6.93018C21.859 5.90004 21.1312 4.86038 20.3342 4.02047C19.3087 2.93945 17.2289 1.4383 16.4603 1.00195M5 20L6 27.3333H11V32M27 20L26 27.3333H21V32M26 27.5V32M6 27.5V32M23 23L9 22.9973L23 23.0054V23Z" />
    </g>
  </svg>
);

const IconBalcony = () => (
  <svg viewBox="0 0 32 32">
    <path d="M4 26H28" />
    <path d="M10 14V26" />
    <path d="M10 2V14" />
    <path d="M22 2V14" />
    <path d="M16 14V26" />
    <path d="M2 14L30 14" />
    <path d="M10 2H21" />
    <path d="M22 14V26" />
    <path d="M4 14V30H28V14" />
  </svg>
);

const IconSize = () => (
  <svg viewBox="0 0 32 32">
    <path d="M1 9V1h8M23 1h8v8M31 23v8h-8M9 31H1v-8" />
  </svg>
);

const IconGenderRestriction = () => (
  <svg viewBox="0 0 32 32">
    <path d="M10.082 30V21.4426M23.8689 8.13115L31 1M31 1H24.8197M31 1V7.18033" />
    <path d="M10.082 21.4426C14.5455 21.4426 18.1639 17.8242 18.1639 13.3607C18.1639 8.89711 14.5455 5.27869 10.082 5.27869C5.61842 5.27869 2 8.89711 2 13.3607C2 17.8242 5.61842 21.4426 10.082 21.4426Z" />
    <path d="M17.6885 21.4426C22.1521 21.4426 25.7705 17.8242 25.7705 13.3607C25.7705 8.89711 22.1521 5.27869 17.6885 5.27869C13.225 5.27869 9.60657 8.89711 9.60657 13.3607C9.60657 17.8242 13.225 21.4426 17.6885 21.4426Z" />
    <path d="M6.75409 26.1967H13.4098" />
  </svg>
);

export default SpaceCategoryFeatures;
